import { motion, useInView } from 'framer-motion'
import { useRef } from 'react'
import {
  Target, Heart, Zap, ArrowRight, MapPin, Building2
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from '../i18n/Link'
import SEO, { schemas } from '../components/SEO'

const valueIcons = [Target, Heart, Zap]

const statValues = ['10K+', '50+', '99.9%', '24/7']

const teamMembers = [
  { name: 'Doğan Cihat Kaya', image: 'DCK' },
  { name: 'Murat Baş', image: 'MB' },
  { name: 'Elif Arslan', image: 'EA' },
  { name: 'Burak Şahin', image: 'BŞ' },
  { name: 'Ayşe Çelik', image: 'AÇ' },
  { name: 'Emre Yıldırım', image: 'EY' },
]

const milestoneYears = ['2020', '2021', '2022', '2023', '2024', '2025']

export default function About() {
  const { t } = useTranslation(['about', 'common'])
  const ref = useRef(null)
  useInView(ref, { once: true, margin: "-100px" })

  const statLabels = t('stats', { returnObjects: true }) as string[]
  const stats = statValues.map((value, i) => ({ value, label: statLabels[i] }))

  const valueTexts = t('values', { returnObjects: true }) as { title: string; description: string }[]
  const values = valueTexts.map((v, i) => ({ icon: valueIcons[i], ...v }))

  const roles = t('team.roles', { returnObjects: true }) as string[]
  const team = teamMembers.map((m, i) => ({ ...m, role: roles[i] }))

  const milestoneTexts = t('timeline.items', { returnObjects: true }) as { title: string; desc: string }[]
  const milestones = milestoneYears.map((year, i) => ({ year, ...milestoneTexts[i] }))

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}

      className="pt-32 pb-24"
    >
      <SEO
        title={t('seo.title')}
        description={t('seo.description')}
        keywords={t('seo.keywords')}
        canonical="/about"
        structuredData={schemas.breadcrumb([{ name: t('common:pages.home'), url: '/' }, { name: t('common:pages.about'), url: '/about' }])}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-20"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary/10 border border-primary/20 rounded-full text-primary text-sm font-medium mb-4">
            <Building2 className="w-4 h-4" />
            {t('hero.eyebrow')}
          </span>
          <h1 className="text-5xl md:text-6xl font-display font-bold mb-6">
            {t('hero.titleStart')}{' '}
            <span className="text-gradient">{t('hero.titleHighlight')}</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto">
            {t('hero.text')}
          </p>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20"
        >
          {stats.map((stat) => (
            <div key={stat.label} className="glass rounded-2xl p-6 text-center">
              <div className="text-4xl font-bold text-gradient mb-2">{stat.value}</div>
              <div className="text-gray-400">{stat.label}</div>
            </div>
          ))}
        </motion.div>

        {/* Values */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          {values.map((value, idx) => (
            <motion.div
              key={value.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="glass rounded-2xl p-8"
            >
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                <value.icon className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">{value.title}</h3>
              <p className="text-gray-400 leading-relaxed">{value.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Timeline */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h2 className="text-3xl font-display font-bold text-white text-center mb-12">
            {t('timeline.title')}
          </h2>
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-gradient-to-b from-primary to-secondary hidden md:block" />
            <div className="space-y-12">
              {milestones.map((milestone, idx) => (
                <motion.div
                  key={milestone.year}
                  initial={{ opacity: 0, x: idx % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className={`flex items-center gap-8 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                >
                  <div className="flex-1 glass rounded-xl p-6">
                    <div className="text-primary font-bold text-lg mb-2">{milestone.year}</div>
                    <h3 className="text-white font-semibold mb-2">{milestone.title}</h3>
                    <p className="text-gray-400 text-sm">{milestone.desc}</p>
                  </div>
                  <div className="w-4 h-4 rounded-full bg-primary hidden md:block relative z-10" />
                  <div className="flex-1 hidden md:block" />
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Team */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h2 className="text-3xl font-display font-bold text-white text-center mb-12">
            {t('team.title')}
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {team.map((member, idx) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="glass rounded-xl p-6 text-center"
              >
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center mx-auto mb-4 text-white font-bold text-xl">
                  {member.image}
                </div>
                <h3 className="text-lg font-semibold text-white">{member.name}</h3>
                <p className="text-gray-400 text-sm">{member.role}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Office */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass rounded-3xl p-8 md:p-12 text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-6">
            <MapPin className="w-4 h-4 text-primary" />
            <span className="text-primary text-sm font-medium">{t('office.eyebrow')}</span>
          </div>
          <h2 className="text-3xl font-display font-bold text-white mb-4">
            {t('office.title')}
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            {t('office.text')}
          </p>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <h2 className="text-3xl font-display font-bold text-white mb-6">
            {t('cta.title')}
          </h2>
          <p className="text-gray-400 mb-8">
            {t('cta.text')}
          </p>
          <Link
            to="/careers"
            className="inline-flex items-center gap-2 px-8 py-4 bg-primary hover:bg-primary-dark text-white font-semibold rounded-xl transition-all duration-200"
          >
            {t('cta.button')}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </motion.div>
      </div>
    </motion.div>
  )
}
